import { DetailedCoin } from '../types';

export interface CoinValuation {
  faceValue: number;
  marketValue: number;
  markup: number;
  category: 'Common' | 'U5M' | 'U1M' | 'U500k' | 'U100k' | 'NIFC';
}

export function getFaceValue(denomination?: string): number {
  switch (denomination) {
    case '2euro': return 2.00;
    case '1euro': return 1.00;
    case '50cent': return 0.50;
    case '20cent': return 0.20;
    case '10cent': return 0.10;
    case '5cent': return 0.05;
    case '2cent': return 0.02;
    case '1cent': return 0.01;
    default: return 2.00;
  }
}

/**
 * Calculates market sell value for a coin based on mintage tier.
 */
export function calculateCoinValue(coin: DetailedCoin | { mintage: number; denomination?: string; type?: string }): CoinValuation {
  const faceValue = getFaceValue(coin.denomination);
  const mintage = coin.mintage ?? 0;

  // 1. NIFC / Set Only (Mintage 0)
  if (mintage === 0) {
    return {
      faceValue,
      marketValue: 200.00,
      markup: Number((200.00 - faceValue).toFixed(2)),
      category: 'NIFC'
    };
  }

  // 2. U100k (<= 100,000)
  if (mintage <= 100_000) {
    // Linear scale between 50k and 100k
    const ratio = Math.max(0, Math.min(1, (100_000 - mintage) / 100_000));
    const markup = Number((35.00 + ratio * 60.00).toFixed(2)); // €35.00 - €95.00
    return {
      faceValue,
      marketValue: Number((faceValue + markup).toFixed(2)),
      markup,
      category: 'U100k'
    };
  }

  // 3. U500k (100,000 to 500,000)
  if (mintage <= 500_000) {
    const ratio = Math.max(0, Math.min(1, (500_000 - mintage) / 400_000));
    const markup = Number((8.00 + ratio * 17.00).toFixed(2)); // €8.00 - €25.00
    return {
      faceValue,
      marketValue: Number((faceValue + markup).toFixed(2)),
      markup,
      category: 'U500k'
    };
  }

  // 4. U1M (500,000 to 1,000,000)
  if (mintage <= 1_000_000) {
    const ratio = Math.max(0, Math.min(1, (1_000_000 - mintage) / 500_000));
    const markup = Number((3.00 + ratio * 3.00).toFixed(2)); // €3.00 - €6.00
    return {
      faceValue,
      marketValue: Number((faceValue + markup).toFixed(2)),
      markup,
      category: 'U1M'
    };
  }

  // 5. U5M (1,000,000 to 5,000,000)
  if (mintage <= 5_000_000) {
    const ratio = Math.max(0, Math.min(1, (5_000_000 - mintage) / 4_000_000));
    const markup = Number((0.30 + ratio * 0.70).toFixed(2)); // €0.30 - €1.00
    return {
      faceValue,
      marketValue: Number((faceValue + markup).toFixed(2)),
      markup,
      category: 'U5M'
    };
  }

  // 6. Common (> 5,000,000)
  return {
    faceValue,
    marketValue: faceValue,
    markup: 0.00,
    category: 'Common'
  };
}
